import React, { useRef, useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Text,
  VStack,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  HStack,
  Checkbox,
  Skeleton,
  Switch,
} from '@chakra-ui/react';
import { useMutation, useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Camera,
  Key,
  Trash,
  MagnifyingGlass,
  Buildings,
  Check,
  CheckCircle,
  Copy,
  EnvelopeSimple,
  Link as LinkIcon,
  Eye,
} from 'phosphor-react';
import { InputForm } from '../../inputs/forms/InputForm';
import { useUsers } from '../../../hooks/useUsers';
import { ICompany } from '../../../hooks/useCompanies';
import { api } from '../../../services/api';

// URL de login da Área VIP (portal do cliente)
const SECAO_CLIENTE_LOGIN_URL = 'https://areadocliente.redeespecialistas.com.br/';

// Interface para dados de acesso criados
interface IDadosAcessoCriado {
  nome: string;
  email: string;
  senha: string;
}

interface IForm {
  id?: number;
  nome: string;
  email: string;
  password?: string;
  confirmPassword?: string;
  ativo?: boolean;
  imagem_url?: string;
  empresas_ids?: number[];
}

const schema = yup.object().shape({
  id: yup.number(),
  nome: yup.string().required('Este campo é obrigatório!'),
  email: yup.string().email('E-mail inválido').required('Este campo é obrigatório!'),
  password: yup.string(),
  confirmPassword: yup.string().oneOf([yup.ref('password'), ''], 'As senhas não coincidem'),
  ativo: yup.boolean(),
  imagem_url: yup.string(),
  empresas_ids: yup.array().of(yup.number().required()).default([]),
});

interface ClientesFormProps {
  defaultValues?: IForm;
}

const generatePassword = () => {
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const special = '!@#$%&*';

  let password = '';
  // Garantir pelo menos um de cada tipo
  password += upper.charAt(Math.floor(Math.random() * upper.length));
  password += numbers.charAt(Math.floor(Math.random() * numbers.length));
  password += special.charAt(Math.floor(Math.random() * special.length));

  // Completar com caracteres aleatórios
  const allChars = lower + upper + numbers + special;
  for (let i = 0; i < 5; i += 1) {
    password += allChars.charAt(Math.floor(Math.random() * allChars.length));
  }

  // Embaralhar a senha
  return password
    .split('')
    .sort(() => Math.random() - 0.5)
    .join('');
};

// Interface para resposta paginada do endpoint /api/v1/empresas/
// Resposta: { data: { items, total, page, size, pages, next_page } }
interface IEmpresasPaginatedResponse {
  items: ICompany[];
  total: number;
  page: number;
  size: number;
  pages: number;
  next_page?: number;
}

interface IEmpresasApiResponse {
  data: IEmpresasPaginatedResponse;
}

// Função para buscar empresas no endpoint /api/v1/empresas/
const fetchEmpresas = async (params: {
  search?: string;
  page?: number;
  size?: number;
}): Promise<IEmpresasApiResponse> => {
  const res = await api.get<IEmpresasApiResponse>('/api/v1/empresas/', { params });
  return res.data;
};

// Interface de tela da Área VIP
interface IAreavipTela {
  codigo: string;
  nome: string;
  path: string;
  icone: string | null;
}

// Buscar telas disponíveis da Área VIP
const fetchTelasDisponiveis = async (): Promise<IAreavipTela[]> => {
  const res = await api.get('/api/v1/secao-cliente/telas-disponiveis/');
  return res.data?.data ?? [];
};

// Buscar telas permitidas de um cliente
const fetchTelasCliente = async (clienteId: number): Promise<IAreavipTela[]> => {
  const res = await api.get(`/api/v1/secao-cliente/${clienteId}/telas/`);
  return res.data?.data ?? [];
};

// Atualizar telas permitidas de um cliente
const atualizarTelasCliente = async (clienteId: number, telaCodigos: string[]) => {
  const res = await api.put(`/api/v1/secao-cliente/${clienteId}/telas/`, {
    tela_codigos: telaCodigos,
  });
  return res.data;
};

// Interface para cadastro de cliente na seção cliente
interface ICadastroCliente {
  nome: string;
  email: string;
  password: string;
  empresas_ids: number[];
}

// Interface de resposta do cadastro de cliente
interface IClienteResponse {
  meta: {
    timestamp: string;
  };
  data: {
    id: number;
    guid: string;
    nome: string;
    email: string;
    cpf: string | null;
    ativo: boolean;
    empresas: Array<{
      id: number;
      razao_social: string;
      nome_fantasia: string;
      cnpj: string;
    }>;
  };
  message: string;
}

// Função para cadastrar cliente na seção cliente
const cadastrarCliente = async (data: ICadastroCliente): Promise<IClienteResponse> => {
  const res = await api.post<IClienteResponse>('/api/v1/secao-cliente/cadastro/', data);
  return res.data;
};

// Função para atualizar cliente na seção cliente
const atualizarCliente = async (
  id: number,
  data: Partial<ICadastroCliente>,
): Promise<IClienteResponse> => {
  const res = await api.put<IClienteResponse>(`/api/v1/secao-cliente/${id}/`, data);
  return res.data;
};

export default function ClientesForm({ defaultValues }: ClientesFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uploadImage, deleteImage } = useUsers();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(defaultValues?.imagem_url);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRemovingImage, setIsRemovingImage] = useState(false);
  const [searchEmpresa, setSearchEmpresa] = useState('');
  const [selectedEmpresas, setSelectedEmpresas] = useState<number[]>(
    defaultValues?.empresas_ids ?? [],
  );

  // Sincronizar selectedEmpresas quando defaultValues mudar (edição)
  useEffect(() => {
    if (defaultValues?.empresas_ids) {
      setSelectedEmpresas(defaultValues.empresas_ids);
    }
  }, [defaultValues?.empresas_ids]);

  // Modal de dados de acesso criados
  const {
    isOpen: isSuccessModalOpen,
    onOpen: onOpenSuccessModal,
    onClose: onCloseSuccessModal,
  } = useDisclosure();
  const [dadosAcessoCriado, setDadosAcessoCriado] = useState<IDadosAcessoCriado | null>(null);

  const isNewCliente = !defaultValues;

  // Debounce do termo de busca
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchEmpresa);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchEmpresa]);

  // Ref para o container de scroll
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  // Buscar empresas com scroll infinito (endpoint /api/v1/empresas/)
  const {
    data: empresasData,
    isFetching: isFetchingEmpresas,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['empresas-clientes', { search: debouncedSearch || undefined, size: 12 }],
    queryFn: ({ pageParam = 1 }) =>
      fetchEmpresas({
        search: debouncedSearch || undefined,
        page: pageParam,
        size: 12,
      }),
    initialPageParam: 1,
    getNextPageParam: lastPage => lastPage?.data?.next_page,
  });

  // Combina todas as páginas em uma única lista
  const empresas = React.useMemo(() => {
    if (!empresasData?.pages) return [];
    const seenIds = new Set<number>();
    const items: ICompany[] = [];
    empresasData.pages.forEach(page => {
      page?.data?.items?.forEach(empresa => {
        if (!seenIds.has(empresa.id)) {
          seenIds.add(empresa.id);
          items.push(empresa);
        }
      });
    });
    return items;
  }, [empresasData?.pages]);

  const totalEmpresas = empresasData?.pages?.[0]?.data?.total ?? 0;

  // ── Telas da Área VIP ──
  const [selectedTelas, setSelectedTelas] = useState<string[]>([]);

  const { data: telasDisponiveis, isFetching: isFetchingTelas } = useQuery<IAreavipTela[]>({
    queryKey: ['areavip-telas-disponiveis'],
    queryFn: fetchTelasDisponiveis,
    enabled: !isNewCliente,
  });

  const { data: telasCliente, isFetching: isFetchingTelasCliente } = useQuery<IAreavipTela[]>({
    queryKey: ['areavip-telas-cliente', defaultValues?.id],
    queryFn: () => fetchTelasCliente(defaultValues!.id!),
    enabled: !isNewCliente && !!defaultValues?.id,
  });

  useEffect(() => {
    if (telasCliente) {
      setSelectedTelas(telasCliente.map(t => t.codigo));
    }
  }, [telasCliente]);

  const handleToggleTela = (codigo: string) => {
    setSelectedTelas(prev =>
      prev.includes(codigo) ? prev.filter(c => c !== codigo) : [...prev, codigo],
    );
  };

  const handleToggleTodasTelas = () => {
    if (!telasDisponiveis) return;
    if (selectedTelas.length === telasDisponiveis.length) {
      setSelectedTelas([]);
    } else {
      setSelectedTelas(telasDisponiveis.map(t => t.codigo));
    }
  };

  // Handler para scroll infinito - carrega mais quando chega perto do final
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    // Carrega mais quando falta 100px para o final
    if (scrollHeight - scrollTop - clientHeight < 100) {
      fetchNextPage();
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const { mutateAsync: mutateCreate } = useMutation({
    mutationFn: cadastrarCliente,
  });

  const { mutateAsync: mutateUpdate } = useMutation({
    mutationFn: (data: Partial<ICadastroCliente> & { id: number }) =>
      atualizarCliente(data.id, data),
  });

  const formMethods = useForm<IForm>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      id: defaultValues?.id,
      nome: defaultValues?.nome ?? '',
      email: defaultValues?.email ?? '',
      password: undefined,
      confirmPassword: undefined,
      empresas_ids: defaultValues?.empresas_ids ?? [],
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Tipo de arquivo não permitido. Use: JPEG, PNG, GIF ou WebP.', {
          position: 'top-right',
        });
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Arquivo muito grande. Tamanho máximo: 5MB.', { position: 'top-right' });
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleGeneratePassword = () => {
    const password = generatePassword();
    formMethods.setValue('password', password);
    formMethods.setValue('confirmPassword', password);
    toast.success('Senha gerada com sucesso!', {
      position: 'top-right',
    });
  };

  // Função para copiar dados de acesso
  const handleCopyDadosAcesso = () => {
    if (!dadosAcessoCriado) return;

    const texto = `Olá ${dadosAcessoCriado.nome},

Seus dados de acesso ao sistema Especialistas:

E-mail: ${dadosAcessoCriado.email}
Senha: ${dadosAcessoCriado.senha}
Link de acesso: ${SECAO_CLIENTE_LOGIN_URL}

Atenciosamente,
Equipe Especialistas`;

    navigator.clipboard.writeText(texto);
    toast.success('Dados de acesso copiados!', { position: 'top-right' });
  };

  // Função para fechar modal, resetar formulário e navegar
  const handleCloseSuccessModal = () => {
    // Resetar formulário
    formMethods.reset({
      id: undefined,
      nome: '',
      email: '',
      password: undefined,
      confirmPassword: undefined,
      empresas_ids: [],
    });
    // Resetar estados
    setSelectedEmpresas([]);
    setSelectedFile(null);
    setPreviewUrl(undefined);
    setSearchEmpresa('');
    // Fechar modal e navegar
    onCloseSuccessModal();
    setDadosAcessoCriado(null);
    navigate('/configuracoes/clientes', { replace: true });
  };

  const handleRemoveImage = async () => {
    if (!defaultValues?.id) {
      setSelectedFile(null);
      setPreviewUrl(undefined);
      return;
    }

    setIsRemovingImage(true);
    try {
      await deleteImage(defaultValues.id);
      setSelectedFile(null);
      setPreviewUrl(undefined);
      queryClient.invalidateQueries({ queryKey: ['clientes'] });
      toast.success('Imagem removida com sucesso!', {
        position: 'top-right',
      });
    } catch (error: any) {
      let errorMessage = 'Erro ao remover imagem.';
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      }
      toast.error(errorMessage, {
        position: 'top-right',
      });
    } finally {
      setIsRemovingImage(false);
    }
  };

  const handleToggleEmpresa = (empresaId: number) => {
    setSelectedEmpresas(prev => {
      if (prev.includes(empresaId)) {
        return prev.filter(id => id !== empresaId);
      }
      return [...prev, empresaId];
    });
  };

  const onSubmit = async (values: IForm) => {
    if (isNewCliente && !values.password) {
      toast.error('Gere uma senha para o cliente antes de salvar.', { position: 'top-right' });
      return;
    }

    if (selectedEmpresas.length === 0) {
      toast.error('Selecione ao menos uma empresa para o cliente.', { position: 'top-right' });
      return;
    }

    setIsSubmitting(true);
    try {
      const senhaGerada = values.password;
      let clienteId: number | undefined;

      if (isNewCliente) {
        // Criar cliente - POST /api/v1/secao-cliente/cadastro/
        const result = await mutateCreate({
          nome: values.nome,
          email: values.email,
          password: values.password!,
          empresas_ids: selectedEmpresas,
        });
        clienteId = result?.data?.id;
      } else {
        // Atualizar cliente - PUT /api/v1/secao-cliente/cadastro/{id}/
        await mutateUpdate({
          id: defaultValues!.id!,
          nome: values.nome,
          email: values.email,
          empresas_ids: selectedEmpresas,
          ...(values.password && { password: values.password }),
        });
        clienteId = defaultValues?.id;
      }

      // Upload de imagem se houver
      if (selectedFile && clienteId) {
        try {
          await uploadImage(clienteId, selectedFile);
        } catch (uploadError: any) {
          let uploadErrorMessage = 'Erro ao fazer upload da imagem.';
          const uploadDetail = uploadError?.response?.data?.detail;
          if (typeof uploadDetail === 'string') {
            uploadErrorMessage = uploadDetail;
          }
          toast.error(uploadErrorMessage, {
            position: 'top-right',
          });
        }
      }

      // Salvar telas permitidas (apenas na edição)
      if (!isNewCliente && clienteId) {
        try {
          await atualizarTelasCliente(clienteId, selectedTelas);
        } catch {
          toast.warning('Cliente salvo, mas houve erro ao atualizar telas da Área VIP.', {
            position: 'top-right',
          });
        }
      }

      queryClient.invalidateQueries({ queryKey: ['clientes'] });

      // Se é criação de novo cliente, mostra modal com dados de acesso
      if (isNewCliente && senhaGerada) {
        setDadosAcessoCriado({
          nome: values.nome,
          email: values.email,
          senha: senhaGerada,
        });
        onOpenSuccessModal();
      } else {
        // Edição - apenas mostra toast e navega
        toast.success('Cliente editado com sucesso!', {
          position: 'top-right',
        });
        navigate('/configuracoes/clientes', { replace: true });
      }
    } catch (error: any) {
      let errorMessage = 'Erro interno no sistema, tente novamente mais tarde.';
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail[0]?.msg || errorMessage;
      }
      toast.error(errorMessage, { position: 'top-right' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const passwordValue = formMethods.watch('password') || '';

  return (
    <Box maxW="1000px" mx="auto">
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormProvider {...formMethods}>
          <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
            {/* Header com Avatar */}
            <Box bg="gray.50" py={8} px={6}>
              <Flex direction="column" align="center" gap={3}>
                <Box position="relative">
                  <Avatar
                    size="2xl"
                    name={formMethods.watch('nome') || 'Cliente'}
                    src={previewUrl}
                    cursor="pointer"
                    onClick={() => fileInputRef.current?.click()}
                    bg="primary"
                    color="white"
                  />
                  <IconButton
                    aria-label="Alterar foto"
                    icon={<Camera size={18} weight="bold" />}
                    size="sm"
                    colorScheme="blue"
                    borderRadius="full"
                    position="absolute"
                    bottom={1}
                    right={1}
                    onClick={() => fileInputRef.current?.click()}
                  />
                  {(previewUrl || selectedFile) && (
                    <IconButton
                      aria-label="Remover foto"
                      icon={<Trash size={18} weight="bold" />}
                      size="sm"
                      colorScheme="red"
                      borderRadius="full"
                      position="absolute"
                      bottom={1}
                      left={1}
                      onClick={handleRemoveImage}
                      isLoading={isRemovingImage}
                    />
                  )}
                </Box>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleFileSelect}
                  display="none"
                />
                <VStack spacing={0}>
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    {formMethods.watch('nome') || 'Nome do Cliente'}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    {formMethods.watch('email') || 'email@exemplo.com'}
                  </Text>
                </VStack>
                {selectedFile && (
                  <Text fontSize="xs" color="green.500" fontWeight="medium">
                    Nova imagem selecionada: {selectedFile.name}
                  </Text>
                )}
              </Flex>
            </Box>

            {/* Formulário */}
            <Box p={6}>
              <VStack spacing={6} align="stretch">
                {/* Dados do Cliente */}
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={4}>
                    DADOS DO CLIENTE
                  </Text>
                  <VStack spacing={4}>
                    <InputForm name="nome" label="Nome completo" placeholder="Digite o nome" />
                    <InputForm name="email" label="E-mail" placeholder="Digite o e-mail" />
                  </VStack>
                </Box>

                <Divider />

                {/* Senha */}
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                        {isNewCliente ? 'SENHA DE ACESSO' : 'ALTERAR SENHA'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {isNewCliente
                          ? 'Clique em "Gerar senha" para criar uma senha segura'
                          : 'Digite uma nova senha ou clique em "Gerar senha"'}
                      </Text>
                    </Box>
                    <Button
                      size="sm"
                      colorScheme="blue"
                      leftIcon={<Key size={16} />}
                      onClick={handleGeneratePassword}
                    >
                      Gerar senha
                    </Button>
                  </Flex>

                  {/* Campos de senha - somente edição permite digitação manual */}
                  {!isNewCliente && (
                    <VStack spacing={3} mb={3}>
                      <InputForm
                        name="password"
                        type="password"
                        label="Nova senha"
                        placeholder="Digite a nova senha ou clique em gerar"
                      />
                      <InputForm
                        name="confirmPassword"
                        type="password"
                        label="Confirmar nova senha"
                        placeholder="Confirme a nova senha"
                      />
                    </VStack>
                  )}

                  {passwordValue && (
                    <Box
                      bg="green.50"
                      p={3}
                      borderRadius="lg"
                      border="1px solid"
                      borderColor="green.200"
                    >
                      <Flex align="center" gap={2}>
                        <Check size={16} color="green" weight="bold" />
                        <Text fontSize="sm" color="green.700">
                          {isNewCliente
                            ? 'Senha gerada com sucesso'
                            : 'Nova senha definida - será alterada ao salvar'}
                        </Text>
                      </Flex>
                    </Box>
                  )}
                </Box>

                <Divider />

                {/* Empresas */}
                <Box>
                  <Flex justify="space-between" align="center" mb={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                        EMPRESAS ASSOCIADAS
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Selecione as empresas que o cliente terá acesso
                      </Text>
                    </Box>
                    {selectedEmpresas.length > 0 && (
                      <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="full">
                        {selectedEmpresas.length} selecionada
                        {selectedEmpresas.length > 1 ? 's' : ''}
                      </Badge>
                    )}
                  </Flex>

                  {/* Campo de busca */}
                  <InputGroup mb={4}>
                    <InputLeftElement pointerEvents="none">
                      <MagnifyingGlass size={20} color="#A0AEC0" />
                    </InputLeftElement>
                    <Input
                      placeholder="Buscar por nome, razão social ou CNPJ..."
                      value={searchEmpresa}
                      onChange={e => setSearchEmpresa(e.target.value)}
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.200"
                      _focus={{
                        bg: 'white',
                        borderColor: 'blue.400',
                        boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)',
                      }}
                    />
                  </InputGroup>

                  {/* Grid de empresas */}
                  <Box
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="xl"
                    p={4}
                    bg="gray.50"
                    maxH="400px"
                    overflowY="auto"
                    css={{
                      '&::-webkit-scrollbar': {
                        width: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        background: '#c1c1c1',
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb:hover': {
                        background: '#a1a1a1',
                      },
                    }}
                  >
                    {isFetchingEmpresas && empresas.length === 0 && (
                      <Flex justify="center" align="center" py={12}>
                        <Spinner size="lg" color="blue.500" thickness="3px" />
                        <Text ml={4} color="gray.500" fontSize="sm">
                          Carregando empresas...
                        </Text>
                      </Flex>
                    )}

                    {!isFetchingEmpresas && empresas.length === 0 && !isFetchingNextPage && (
                      <Flex
                        direction="column"
                        justify="center"
                        align="center"
                        py={12}
                        color="gray.400"
                      >
                        <Icon as={Buildings} boxSize={12} mb={3} />
                        <Text fontSize="sm" fontWeight="medium">
                          {searchEmpresa
                            ? 'Nenhuma empresa encontrada'
                            : 'Nenhuma empresa cadastrada'}
                        </Text>
                        {searchEmpresa && (
                          <Text fontSize="xs" color="gray.400" mt={1}>
                            Tente buscar por outro termo
                          </Text>
                        )}
                      </Flex>
                    )}

                    {empresas.length > 0 && (
                      <>
                        <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                          {empresas.map((empresa: ICompany) => {
                            const isSelected = selectedEmpresas.includes(empresa.id);
                            return (
                              <Box
                                key={empresa.id}
                                position="relative"
                                p={4}
                                bg={isSelected ? 'blue.50' : 'white'}
                                border="2px solid"
                                borderColor={isSelected ? 'blue.400' : 'gray.200'}
                                borderRadius="lg"
                                cursor="pointer"
                                transition="all 0.2s ease"
                                _hover={{
                                  borderColor: isSelected ? 'blue.500' : 'blue.300',
                                  transform: 'translateY(-2px)',
                                  boxShadow: 'md',
                                }}
                                onClick={() => handleToggleEmpresa(empresa.id)}
                              >
                                {/* Indicador de seleção */}
                                {isSelected && (
                                  <Box position="absolute" top={2} right={2} color="blue.500">
                                    <CheckCircle size={22} weight="fill" />
                                  </Box>
                                )}

                                {/* Ícone centralizado */}
                                <Flex
                                  w={10}
                                  h={10}
                                  bg={isSelected ? 'blue.100' : 'gray.100'}
                                  borderRadius="full"
                                  align="center"
                                  justify="center"
                                  mx="auto"
                                  mb={2}
                                >
                                  <Icon
                                    as={Buildings}
                                    boxSize={5}
                                    color={isSelected ? 'blue.600' : 'gray.500'}
                                  />
                                </Flex>

                                {/* External ID centralizado */}
                                <Text
                                  fontSize="xs"
                                  fontWeight="bold"
                                  color={isSelected ? 'blue.600' : 'gray.600'}
                                  textAlign="center"
                                  mb={2}
                                >
                                  ID {empresa.external_id || empresa.id}
                                </Text>

                                {/* Nome da empresa */}
                                <Text
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color={isSelected ? 'blue.700' : 'gray.700'}
                                  noOfLines={2}
                                  lineHeight="short"
                                  textAlign="center"
                                  mb={1}
                                >
                                  {empresa.nome || empresa.razao_social}
                                </Text>

                                {/* CNPJ */}
                                <Text
                                  fontSize="xs"
                                  color={isSelected ? 'blue.500' : 'gray.500'}
                                  fontFamily="mono"
                                  textAlign="center"
                                >
                                  {empresa.cnpj
                                    ?.padStart(14, '0')
                                    .replace(
                                      /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
                                      '$1.$2.$3/$4-$5',
                                    )}
                                </Text>
                              </Box>
                            );
                          })}
                        </Grid>

                        {/* Indicador de carregamento */}
                        {isFetchingNextPage && (
                          <Flex align="center" justify="center" gap={2} py={3}>
                            <Spinner size="sm" color="blue.500" />
                            <Text fontSize="xs" color="gray.500">
                              Carregando mais...
                            </Text>
                          </Flex>
                        )}
                      </>
                    )}
                  </Box>

                  {/* Contador de total */}
                  {!isFetchingEmpresas && totalEmpresas > 0 && (
                    <Text fontSize="xs" color="gray.500" mt={2} textAlign="right">
                      Mostrando {empresas.length} de {totalEmpresas} empresas
                    </Text>
                  )}
                </Box>
              </VStack>
            </Box>

            {/* Telas da Área VIP (apenas na edição) */}
            {!isNewCliente && (
              <Box p={6} borderTop="1px" borderColor="gray.100">
                <Flex align="center" gap={2} mb={4}>
                  <Icon as={Eye} boxSize={5} color="blue.500" />
                  <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                    TELAS VISÍVEIS NA ÁREA VIP
                  </Text>
                </Flex>

                {(isFetchingTelas || isFetchingTelasCliente) && (
                  <VStack spacing={2}>
                    <Skeleton height="24px" width="100%" borderRadius="md" />
                    <Skeleton height="24px" width="100%" borderRadius="md" />
                    <Skeleton height="24px" width="100%" borderRadius="md" />
                  </VStack>
                )}

                {!isFetchingTelas &&
                  !isFetchingTelasCliente &&
                  telasDisponiveis &&
                  telasDisponiveis.length > 0 && (
                    <VStack spacing={3} align="stretch">
                      <Flex justify="space-between" align="center">
                        <Text fontSize="xs" color="gray.500">
                          Selecione as telas que este cliente poderá acessar
                        </Text>
                        <Flex align="center" gap={2}>
                          <Text fontSize="xs" color="gray.500">
                            Todas
                          </Text>
                          <Switch
                            size="sm"
                            colorScheme="blue"
                            isChecked={selectedTelas.length === telasDisponiveis.length}
                            onChange={handleToggleTodasTelas}
                          />
                        </Flex>
                      </Flex>
                      <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                        {telasDisponiveis.map(tela => (
                          <Checkbox
                            key={tela.codigo}
                            isChecked={selectedTelas.includes(tela.codigo)}
                            onChange={() => handleToggleTela(tela.codigo)}
                            colorScheme="blue"
                            size="md"
                          >
                            <Text fontSize="sm">{tela.nome}</Text>
                          </Checkbox>
                        ))}
                      </Grid>
                      {selectedTelas.length === 0 && (
                        <Text fontSize="xs" color="orange.500">
                          Nenhuma tela selecionada — o cliente verá todas as telas por padrão.
                        </Text>
                      )}
                    </VStack>
                  )}

                {!isFetchingTelas &&
                  !isFetchingTelasCliente &&
                  (!telasDisponiveis || telasDisponiveis.length === 0) && (
                    <Text fontSize="sm" color="gray.400">
                      Nenhuma tela disponível. Acesse a Área VIP para sincronizar as telas.
                    </Text>
                  )}
              </Box>
            )}

            {/* Footer com botões */}
            <Box px={6} py={4} bg="gray.50" borderTop="1px" borderColor="gray.100">
              <Flex justify="space-between">
                <Button variant="ghost" onClick={() => navigate(-1)}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  loadingText="Salvando..."
                >
                  {isNewCliente ? 'Criar Cliente' : 'Salvar Alterações'}
                </Button>
              </Flex>
            </Box>
          </Box>
        </FormProvider>
      </form>

      {/* Modal de sucesso com dados de acesso */}
      <Modal
        isOpen={isSuccessModalOpen}
        onClose={handleCloseSuccessModal}
        size="lg"
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl" mx={4}>
          <ModalHeader pb={2}>
            <VStack spacing={3} align="center" pt={4}>
              <Flex
                w={16}
                h={16}
                bg="green.100"
                borderRadius="full"
                align="center"
                justify="center"
              >
                <CheckCircle size={40} weight="fill" color="#38A169" />
              </Flex>
              <Text fontSize="xl" fontWeight="bold" color="gray.800">
                Cliente criado com sucesso!
              </Text>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Um e-mail com as instruções de acesso foi enviado para o cliente.
              </Text>
            </VStack>
          </ModalHeader>

          <ModalBody py={6}>
            <VStack spacing={4} align="stretch">
              {/* Card com dados de acesso */}
              <Box bg="gray.50" borderRadius="lg" p={5} border="1px solid" borderColor="gray.200">
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="gray.500"
                  mb={4}
                  textTransform="uppercase"
                >
                  Dados de Acesso
                </Text>

                <VStack spacing={3} align="stretch">
                  {/* Email */}
                  <HStack justify="space-between">
                    <HStack spacing={2} color="gray.600">
                      <EnvelopeSimple size={18} />
                      <Text fontSize="sm">E-mail:</Text>
                    </HStack>
                    <Text fontSize="sm" fontWeight="semibold" color="gray.800">
                      {dadosAcessoCriado?.email}
                    </Text>
                  </HStack>

                  {/* Senha */}
                  <HStack justify="space-between">
                    <HStack spacing={2} color="gray.600">
                      <Key size={18} />
                      <Text fontSize="sm">Senha:</Text>
                    </HStack>
                    <Text
                      fontSize="sm"
                      fontWeight="semibold"
                      fontFamily="mono"
                      bg="yellow.100"
                      px={2}
                      py={0.5}
                      borderRadius="md"
                      color="yellow.800"
                    >
                      {dadosAcessoCriado?.senha}
                    </Text>
                  </HStack>

                  {/* Link de acesso */}
                  <HStack justify="space-between">
                    <HStack spacing={2} color="gray.600">
                      <LinkIcon size={18} />
                      <Text fontSize="sm">Link:</Text>
                    </HStack>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                      color="blue.600"
                      maxW="250px"
                      isTruncated
                    >
                      {SECAO_CLIENTE_LOGIN_URL}
                    </Text>
                  </HStack>
                </VStack>
              </Box>

              {/* Botão de copiar dados */}
              <Button
                size="lg"
                colorScheme="blue"
                leftIcon={<Copy size={20} />}
                onClick={handleCopyDadosAcesso}
                w="100%"
              >
                Copiar Dados de Acesso
              </Button>
            </VStack>
          </ModalBody>

          <ModalFooter pt={0} pb={6}>
            <Button variant="ghost" colorScheme="gray" onClick={handleCloseSuccessModal} w="100%">
              Fechar e voltar para lista
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
